import React, { useState } from 'react';
import { MessageCircle, User, Clock, ChevronRight, Send, ArrowLeft } from 'lucide-react';

interface Reply {
  id: string;
  author: string;
  content: string;
  createdAt: string;
}

interface Thread {
  id: string;
  lessonId?: number;
  author: string;
  title: string;
  content: string;
  createdAt: string;
  repliesCount: number;
  lastActivity: string;
}

interface ForumThreadProps {
  thread: Thread;
  replies: Reply[];
  onBack: () => void;
  onReply: (threadId: string, content: string) => Promise<void>;
  isSubmitting?: boolean;
}

export { type Thread, type Reply };

export const ForumThreadView = ({ thread, replies, onBack, onReply, isSubmitting }: ForumThreadProps) => {
  const [replyText, setReplyText] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) {return;}
    await onReply(thread.id, replyText);
    setReplyText('');
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-xs font-bold uppercase tracking-widest">
        <ArrowLeft size={14} /> Back to Discussions
      </button>

      <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-8 space-y-4">
        <div className="flex items-center gap-3 text-[10px] text-slate-400 font-medium">
          <span className="flex items-center gap-1"><User size={12} /> {thread.author}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {new Date(thread.createdAt).toLocaleDateString()}</span>
          <span className="flex items-center gap-1"><MessageCircle size={12} /> {replies.length} replies</span>
        </div>
        <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">{thread.title}</h2>
        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{thread.content}</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 space-y-4">
        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
          <MessageCircle size={16} /> Replies ({replies.length})
        </h3>

        {replies.length === 0 && (
          <p className="text-xs text-slate-400 font-medium py-4 text-center">No replies yet. Be the first to respond!</p>
        )}

        <div className="space-y-4">
          {replies.map((reply) => (
            <div key={reply.id} className="p-4 bg-slate-50 rounded-2xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-600">{reply.author}</span>
                <span className="text-[10px] text-slate-400">{new Date(reply.createdAt).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{reply.content}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="pt-4 border-t border-slate-100 space-y-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write your reply..."
            rows={3}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!replyText.trim() || isSubmitting}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={14} />
              {isSubmitting ? 'Posting...' : 'Post Reply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
